import { Gavel } from "lucide-react";
import { normalizeRound, voteScores } from "@/lib/format";

/**
 * Aggregates votes array into per-round / per-box breakdown.
 * Vote shape (flexible):
 *   { judgeName, ronda, participante, puntos, box, timestamp }
 *   or { judgeName, ronda, a, b, scoreA, scoreB, box }
 */
function aggregate(votes, filterRound) {
  const filtered = filterRound
    ? votes.filter((v) => normalizeRound(v.ronda) === filterRound)
    : votes;

  const byParticipant = {};
  filtered.forEach((v) => {
    Object.entries(voteScores(v)).forEach(([name, pts]) => {
      if (!byParticipant[name]) byParticipant[name] = { total: 0, count: 0 };
      byParticipant[name].total += pts;
      byParticipant[name].count += 1;
    });
  });

  const ranking = Object.entries(byParticipant)
    .map(([name, s]) => ({
      name,
      total: s.total,
      count: s.count,
      avg: s.count ? s.total / s.count : 0,
    }))
    .sort((a, b) => b.total - a.total);

  return { filtered, ranking };
}

export default function LiveVoting({ votes = [], event, torneo }) {
  const currentRound = normalizeRound(torneo?.ronda);
  const maxPoint = event?.maxPointLimit || 10;
  const { filtered, ranking } = aggregate(votes, currentRound || null);
  const maxTotal = ranking.length > 0 ? Math.max(ranking[0].total, 1) : 1;

  const judges = Array.from(
    new Set(filtered.map((v) => v.judgeName).filter(Boolean))
  );

  return (
    <div className="flex flex-col gap-6">
      <div
        data-testid="voting-widget"
        className="border border-[#1E293B] bg-[#0F172A]"
      >
        <div className="flex items-center justify-between border-b border-[#1E293B] px-6 py-4">
          <div className="flex items-center gap-3">
            <Gavel className="h-5 w-5 text-[#FF5500]" />
            <h3 className="font-display text-2xl tracking-wider text-white">
              Votos en vivo
            </h3>
            {currentRound && (
              <span className="border border-[#FF5500] bg-[#080D1A] px-2.5 py-0.5 font-ui text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF5500]">
                {currentRound}
              </span>
            )}
          </div>
          <span
            data-testid="judges-count"
            className="font-ui text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]"
          >
            {judges.length} juez{judges.length === 1 ? "" : "es"} activo
            {judges.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className="p-6">
          {ranking.length === 0 ? (
            <p className="font-ui text-xs uppercase tracking-[0.25em] text-[#94A3B8]">
              Sin votos registrados todavía
            </p>
          ) : (
            <ul className="flex flex-col gap-4">
              {ranking.map((r, idx) => {
                const widthPct = Math.min(100, (r.total / maxTotal) * 100);
                return (
                  <li
                    key={r.name}
                    data-testid={`vote-row-${r.name}`}
                    className="flex items-center gap-4"
                  >
                    <span className="w-8 font-display text-2xl tracking-wider text-[#94A3B8]">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1">
                      <div className="mb-1.5 flex items-end justify-between gap-3">
                        <span className="font-body text-sm font-semibold text-white">
                          {r.name}
                        </span>
                        <div className="flex items-baseline gap-2">
                          <span className="font-display text-xl tracking-wider text-[#FF5500]">
                            {r.total.toFixed(1)}
                          </span>
                          <span className="font-ui text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">
                            prom {r.avg.toFixed(1)} / {maxPoint}
                          </span>
                        </div>
                      </div>
                      <div className="h-2 w-full border border-[#1E293B] bg-[#080D1A]">
                        <div
                          aria-live="polite"
                          className="h-full"
                          style={{
                            width: `${widthPct}%`,
                            background:
                              "linear-gradient(90deg, #FF5500 0%, #CC3300 100%)",
                            transition: "width 500ms cubic-bezier(0.2,0.8,0.2,1)",
                          }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {judges.length > 0 && (
        <div className="border border-[#1E293B] bg-[#0F172A] p-6">
          <h4 className="mb-4 font-ui text-[10px] font-bold uppercase tracking-[0.25em] text-[#94A3B8]">
            Jueces en acción
          </h4>
          <div className="flex flex-wrap gap-2">
            {judges.map((j) => (
              <span
                key={j}
                data-testid={`judge-${j}`}
                className="border border-[#1E293B] bg-[#080D1A] px-3 py-1.5 font-body text-xs text-white"
              >
                {j}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
